import React, { useState, useMemo, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '@/components/shared/Card';
import Button from '@/components/shared/Button';
import { useKalkulator } from '@/hooks/useKalkulator';
import { useLeads } from '@/hooks/useLeads';
import { apiFetch } from '@/lib/api';
import { getNetzbetreiber, getNetzbetreiberList } from '@/data/netzbetreiberMapping';
import { BRANCHEN, SPANNUNGSEBENEN, ZAEHLVERFAHREN } from '@/data/abgabenUmlagen';
import {
  FiArrowLeft, FiArrowRight, FiCheck, FiZap, FiMapPin,
  FiSliders, FiFileText, FiSave, FiTrash2, FiRefreshCw,
  FiChevronDown, FiChevronUp, FiFilter, FiEye, FiEyeOff,
  FiCalendar, FiDollarSign, FiTrendingDown, FiInfo, FiGrid, FiList,
  FiSend, FiCopy, FiLink, FiMail, FiX,
} from 'react-icons/fi';

const STEP_LABELS = ['Eingabe', 'Aufschlag', 'Zusammenfassung'];
const STEP_ICONS = [FiFileText, FiSliders, FiCheck];

// ============================================================
// Netzentgelt — ehrliche Kennzeichnung der Herkunft
//
// Der Rechenkern liefert im Ergebnis mit, WOHER das Netzentgelt stammt:
//   netzentgelt_herkunft = 'preisblatt' -> echtes Preisblatt des Netzbetreibers
//                                          (netzentgelt_quelle nennt das Blatt)
//   netzentgelt_herkunft = 'schaetzung' -> Regionalpauschale nach 2-stelliger PLZ
// Alles andere (z.B. aeltere gespeicherte Kalkulationen ohne Kennzeichnung) gilt
// als UNGEKENNZEICHNET und wird nie als Preisblatt ausgegeben.
// ============================================================
const NETZENTGELTE_PFAD = '/app/netzentgelte';

const istPreisblatt = (herkunft) => herkunft === 'preisblatt';

/**
 * Netzentgelt formatieren.
 * Ein Schaetzwert bekommt nur ZWEI Nachkommastellen — vier Stellen wuerden eine
 * Genauigkeit vortaeuschen, die eine Regionalpauschale nach 2-stelliger PLZ nicht hat.
 * Fehlt der Wert, steht dort "–" und nie eine 0.
 */
const formatNetzentgelt = (wert, herkunft, stellenBeiPreisblatt = 4) => {
  const num = Number(wert);
  if (wert == null || Number.isNaN(num)) return '–';
  return num.toFixed(istPreisblatt(herkunft) ? stellenBeiPreisblatt : 2);
};

/** Benutzungsstunden = Jahresverbrauch / Jahreshoechstleistung */
const berechneBenutzungsstunden = (verbrauch, leistung) => {
  const v = Number(verbrauch);
  const l = Number(leistung);
  if (!v || !l || Number.isNaN(v) || Number.isNaN(l) || l <= 0) return null;
  return v / l;
};

const formatStunden = (h) =>
  h == null ? '–' : `${Math.round(h).toLocaleString('de-DE')} h/a`;

// ============================================================
// Angebotspreis — nur das, was AIQCON tatsaechlich beeinflusst
//
//   Angebotspreis (Energie) = Beschaffung + AIQCON-Provision
//
// Netzentgelte, Stromsteuer, KWKG-/Offshore-/§19-Umlage und Konzessionsabgabe
// sind regulierte Durchleitungs- bzw. Staatskosten: fuer JEDEN Versorger gleich
// und nicht verhandelbar. Sie werden weiterhin vollstaendig berechnet und
// AUSGEWIESEN — aber als "zzgl." und NICHT in den Angebotspreis eingerechnet.
//
// Der Rechenkern liefert je Laufzeit angebotspreis_ct_kwh / angebotspreis_jahr_eur.
// Aeltere gespeicherte Kalkulationen kennen diese Felder nicht; dort wird der
// Wert aus Energie + Provision rekonstruiert — exakt nach derselben Definition,
// nicht geraten. Fehlt ein Bestandteil, steht "–" und niemals 0.
// ============================================================

/** Einheitliche Bezeichnung — nur an einer Stelle pflegen. */
const ZZGL_TEXT = 'zzgl. Netzentgelte, Abgaben und Umlagen';
const ZZGL_TITLE =
  'Reguliert und fuer jeden Versorger identisch — nicht verhandelbar und deshalb nicht im Angebotspreis enthalten.';

const istZahl = (v) => v != null && !Number.isNaN(Number(v));

/** Angebotspreis in ct/kWh — Feld des Rechenkerns, sonst Energie + Provision. */
const angebotspreisCt = (lz) => {
  if (!lz) return null;
  if (istZahl(lz.angebotspreis_ct_kwh)) return Number(lz.angebotspreis_ct_kwh);
  if (!istZahl(lz.energie_ct_kwh) || !istZahl(lz.provision_ct_kwh)) return null;
  return Number(lz.energie_ct_kwh) + Number(lz.provision_ct_kwh);
};

/** Angebotspreis in EUR/a — Feld des Rechenkerns, sonst aus ct/kWh und Verbrauch. */
const angebotspreisJahr = (lz, verbrauch) => {
  if (!lz) return null;
  if (istZahl(lz.angebotspreis_jahr_eur)) return Number(lz.angebotspreis_jahr_eur);
  const ct = angebotspreisCt(lz);
  const v = Number(verbrauch);
  if (ct == null || !istZahl(verbrauch) || !v) return null;
  return (ct / 100) * v;
};

/**
 * Reichert eine Laufzeit um die beiden Angebotspreis-Felder an.
 * Vorhandene Werte des Rechenkerns werden NIE ueberschrieben — die Anreicherung
 * ist nur die Absicherung fuer geladene Alt-Kalkulationen, damit die
 * Kundenansicht die Felder auf jeden Fall vorfindet.
 */
const mitAngebotspreis = (lz, verbrauch) => {
  if (!lz) return lz;
  const ct = angebotspreisCt(lz);
  const jahr = angebotspreisJahr(lz, verbrauch);
  return {
    ...lz,
    angebotspreis_ct_kwh: ct == null ? null : Math.round(ct * 10000) / 10000,
    angebotspreis_jahr_eur: jahr == null ? null : Math.round(jahr * 100) / 100,
  };
};

/** Zahl in ct/kWh — fehlender Wert wird "–", nie 0. */
const fmtCt = (wert, stellen = 3) =>
  istZahl(wert) ? Number(wert).toFixed(stellen) : '–';

/** Zahl in EUR — fehlender Wert wird "–", nie 0. */
const fmtEUR = (wert) =>
  istZahl(wert)
    ? Number(wert).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '–';

/**
 * Summe der nicht verhandelbaren Positionen (Netz + Abgaben).
 * Nachkommastellen richten sich nach dem Netzentgelt: ein Schaetzwert bekommt
 * nur zwei, sonst wuerde die Summe eine Genauigkeit vortaeuschen.
 */
const fmtZzglSumme = (netz, abgaben, herkunft) => {
  if (!istZahl(netz) || !istZahl(abgaben)) return '–';
  return (Number(netz) + Number(abgaben)).toFixed(istPreisblatt(herkunft) ? 3 : 2);
};

/** Kleines graues Etikett: diese Position ist NICHT im Angebotspreis enthalten. */
const ZzglBadge = () => (
  <span
    className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-gray-200 text-gray-700"
    title={ZZGL_TITLE}
  >
    zzgl.
  </span>
);

/**
 * Nachrangige Information: die geschaetzten Gesamtkosten inkl. Netz und Abgaben.
 * Bewusst klein und grau — das ist NICHT der Angebotspreis. Solange kein
 * Preisblatt hinterlegt ist, wird der Netzanteil als Richtwert benannt.
 */
const GesamtkostenHinweis = ({ herkunft, className = '' }) => (
  <p className={`text-xs text-gray-400 ${className}`}>
    Nachrichtlich, nicht Bestandteil des Angebots.
    {!istPreisblatt(herkunft) && ' Der Netzentgelt-Anteil ist ein regionaler Richtwert, solange kein Preisblatt hinterlegt ist.'}
  </p>
);

/** Kleines Etikett direkt neben der Zahl */
const HerkunftBadge = ({ herkunft }) =>
  istPreisblatt(herkunft) ? (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-teal-100 text-teal-700"
      title="Wert stammt aus einem hinterlegten Preisblatt des Netzbetreibers"
    >
      Preisblatt
    </span>
  ) : (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-amber-100 text-amber-800"
      title="Regionaler Schaetzwert nach 2-stelliger PLZ — kein Preisblatt hinterlegt"
    >
      Schaetzwert
    </span>
  );

/**
 * Ausfuehrliche Herkunfts-Angabe zum Netzentgelt (interne Sicht — darf technisch sein).
 * Zeigt Quelle bzw. Schaetzwert-Warnung, den Hinweis des Rechenkerns und bei RLM
 * die Benutzungsstunden.
 */
const NetzentgeltHerkunft = ({ result, className = '' }) => {
  if (!result) return null;

  const {
    netzentgelt_herkunft: herkunft,
    netzentgelt_quelle: quelle,
    netzentgelt_hinweis: hinweis,
    benutzungsstunden,
    netzbetreiber,
    zaehlverfahren,
    leistung,
    verbrauch,
  } = result;

  const preisblatt = istPreisblatt(herkunft);
  const istRLM = zaehlverfahren === 'RLM';
  const leistungNum = Number(leistung) || 0;
  const stunden = benutzungsstunden != null
    ? Number(benutzungsstunden)
    : berechneBenutzungsstunden(verbrauch, leistungNum);

  return (
    <div
      className={`rounded-lg border p-3 text-xs ${
        preisblatt ? 'bg-teal-50 border-teal-200 text-teal-900' : 'bg-amber-50 border-amber-200 text-amber-900'
      } ${className}`}
    >
      <div className="flex items-start gap-2">
        <FiInfo size={14} className="mt-0.5 flex-shrink-0" />
        <div className="space-y-1">
          {preisblatt ? (
            <>
              <p className="font-semibold">Netzentgelt aus hinterlegtem Preisblatt</p>
              <p>
                Netzbetreiber: {netzbetreiber || 'nicht angegeben'}
                {' · '}
                Quelle: {quelle || 'im Preisblatt nicht angegeben'}
              </p>
            </>
          ) : (
            <>
              <p className="font-semibold">
                {herkunft === 'schaetzung'
                  ? 'Regionaler Schaetzwert (kein Preisblatt hinterlegt)'
                  : 'Regionaler Schaetzwert (Herkunft nicht gekennzeichnet)'}
              </p>
              <p>
                Der Wert ist eine Regionalpauschale nach den ersten beiden PLZ-Ziffern —
                nicht der Tarif von {netzbetreiber || 'diesem Netzbetreiber'}. Deshalb nur
                zwei Nachkommastellen.
              </p>
              <p>
                <Link to={NETZENTGELTE_PFAD} className="underline font-medium">
                  Preisblatt unter Netzentgelte hinterlegen
                </Link>{' '}
                — danach rechnet der Kalkulator mit den echten Werten.
              </p>
            </>
          )}

          {hinweis && <p className="font-medium">Hinweis: {hinweis}</p>}

          {istRLM && (
            leistungNum > 0 ? (
              <p>
                RLM · Benutzungsstunden: {formatStunden(stunden)}
                {' '}(Verbrauch {Number(verbrauch) ? Number(verbrauch).toLocaleString('de-DE') : '–'} kWh
                {' '}/ Leistung {leistungNum.toLocaleString('de-DE')} kW)
              </p>
            ) : (
              <p className="font-medium">
                RLM ohne Jahreshoechstleistung: Ohne kW-Wert lassen sich weder
                Benutzungsstunden noch Leistungspreis rechnen — das Netzentgelt bleibt eine
                reine ct/kWh-Naeherung.
              </p>
            )
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Stepper
// ============================================================
const Stepper = ({ currentStep, onStepClick }) => (
  <div className="flex items-center justify-center mb-8">
    {STEP_LABELS.map((label, idx) => {
      const Icon = STEP_ICONS[idx];
      const isActive = idx === currentStep;
      const isDone = idx < currentStep;
      return (
        <React.Fragment key={idx}>
          {idx > 0 && (
            <div className={`w-16 h-0.5 mx-1 ${isDone ? 'bg-teal-500' : 'bg-gray-200'}`} />
          )}
          <button
            onClick={() => onStepClick(idx)}
            disabled={idx > currentStep}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${isActive ? 'bg-teal-600 text-white shadow-md' : ''}
              ${isDone ? 'bg-teal-100 text-teal-700 hover:bg-teal-200' : ''}
              ${!isActive && !isDone ? 'bg-gray-100 text-gray-400' : ''}
              ${idx <= currentStep ? 'cursor-pointer' : 'cursor-not-allowed'}
            `}
          >
            <Icon size={16} />
            {label}
          </button>
        </React.Fragment>
      );
    })}
  </div>
);

// ============================================================
// Schritt 1: EINGABE
// ============================================================
const EingabeStep = ({ formData, updateForm, onCalculate, isCalculating, priceMode, setPriceMode, futures, calcError }) => {
  const [detectedNB, setDetectedNB] = useState(null);
  const [nbList, setNbList] = useState([]);
  const [selectedNBIndex, setSelectedNBIndex] = useState(0);

  // Der ausgewaehlte Netzbetreiber gehoert in die Formulardaten — nur so kann der
  // Rechenkern das passende Preisblatt suchen statt blind die Regionalpauschale zu nehmen.
  const uebernehmeNetzbetreiber = (nb) => updateForm('netzbetreiber', nb?.name || '');

  const handlePLZChange = (value) => {
    updateForm('plz', value);
    if (value.length >= 2) {
      const list = getNetzbetreiberList(value, formData.spannungsebene);
      setNbList(list);
      setSelectedNBIndex(0);
      setDetectedNB(list[0] || null);
      uebernehmeNetzbetreiber(list[0]);
    } else {
      setNbList([]);
      setDetectedNB(null);
      uebernehmeNetzbetreiber(null);
    }
  };

  const handleNBSelect = (index) => {
    setSelectedNBIndex(index);
    setDetectedNB(nbList[index] || null);
    uebernehmeNetzbetreiber(nbList[index]);
  };

  const handleSpannungsebeneChange = (value) => {
    updateForm('spannungsebene', value);
    if (formData.plz.length >= 2) {
      const list = getNetzbetreiberList(formData.plz, value);
      setNbList(list);
      const gewaehlt = list[selectedNBIndex] || list[0] || null;
      setDetectedNB(gewaehlt);
      uebernehmeNetzbetreiber(gewaehlt);
    }
  };

  const canCalculate = formData.plz.length >= 5 && parseInt(formData.verbrauch) > 0;

  // RLM-Vorschau: Benutzungsstunden = Verbrauch / Jahreshoechstleistung
  const verbrauchNum = parseInt(formData.verbrauch) || 0;
  const leistungNum = parseFloat(String(formData.leistung).replace(',', '.')) || 0;
  const istRLM = formData.zaehlverfahren === 'RLM';
  const benutzungsstundenVorschau = berechneBenutzungsstunden(verbrauchNum, leistungNum);

  return (
    <div className="max-w-5xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hauptformular */}
        <div className="lg:col-span-2">
          <Card>
            <div className="flex items-center gap-2 mb-6">
              <FiZap className="text-teal-600" size={20} />
              <h2 className="text-lg font-bold text-gray-900">LIEFERSTELLEN-DATEN</h2>
            </div>

            <div className="space-y-4">
              {/* Zeile 1: PLZ, Netzbetreiber, Verbrauch */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PLZ *</label>
                  <input
                    type="text"
                    value={formData.plz}
                    onChange={e => handlePLZChange(e.target.value.replace(/\D/g, '').slice(0, 5))}
                    placeholder="z.B. 60311"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    maxLength={5}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Netzbetreiber</label>
                  {nbList.length > 1 ? (
                    <select
                      value={selectedNBIndex}
                      onChange={e => handleNBSelect(parseInt(e.target.value))}
                      className="w-full px-3 py-2 border rounded-lg text-sm bg-teal-50 border-teal-200 text-teal-800 focus:ring-2 focus:ring-teal-500"
                    >
                      {nbList.map((nb, idx) => (
                        <option key={idx} value={idx}>{nb.name}</option>
                      ))}
                    </select>
                  ) : (
                    <div className={`px-3 py-2 border rounded-lg text-sm ${detectedNB ? 'bg-teal-50 border-teal-200 text-teal-800' : 'bg-gray-50 text-gray-400'}`}>
                      {detectedNB ? (
                        <span className="flex items-center gap-1">
                          <FiMapPin size={14} />
                          {detectedNB.name}
                        </span>
                      ) : 'Wird automatisch erkannt'}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Verbrauch/a (kWh) *
                    <span className="ml-1 text-gray-400 cursor-help" title="Jahresverbrauch in Kilowattstunden">&#9432;</span>
                  </label>
                  <input
                    type="text"
                    value={formData.verbrauch}
                    onChange={e => updateForm('verbrauch', e.target.value.replace(/\D/g, ''))}
                    placeholder="z.B. 50000"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>
              </div>

              {/* Zeile 2: Zaehlverfahren, Leistung */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Zaehlverfahren</label>
                  <select
                    value={formData.zaehlverfahren}
                    onChange={e => updateForm('zaehlverfahren', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    {ZAEHLVERFAHREN.map(z => (
                      <option key={z.value} value={z.value}>{z.label}</option>
                    ))}
                  </select>
                </div>

                {istRLM && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Jahreshoechstleistung (kW)
                      <span className="ml-1 text-gray-400 cursor-help" title="Hoechste im Jahr gemessene Leistung — Basis fuer den Leistungspreis des Netzbetreibers">&#9432;</span>
                    </label>
                    <input
                      type="text"
                      value={formData.leistung}
                      onChange={e => updateForm('leistung', e.target.value)}
                      placeholder="z.B. 100"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    />
                    {/* Ehrlich benennen, was ohne kW-Wert nicht gerechnet werden kann */}
                    {leistungNum > 0 ? (
                      <p className="text-xs text-gray-500 mt-1">
                        Benutzungsstunden:{' '}
                        <span className="font-medium text-gray-700">
                          {benutzungsstundenVorschau != null ? formatStunden(benutzungsstundenVorschau) : '–'}
                        </span>
                        {benutzungsstundenVorschau == null && ' (Verbrauch fehlt)'}
                      </p>
                    ) : (
                      <p className="text-xs text-amber-700 mt-1">
                        Ohne Jahreshoechstleistung kann kein Leistungspreis gerechnet werden —
                        das Netzentgelt bleibt dann eine reine ct/kWh-Naeherung.
                      </p>
                    )}
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Spannungsebene</label>
                  <select
                    value={formData.spannungsebene}
                    onChange={e => handleSpannungsebeneChange(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    {SPANNUNGSEBENEN.map(s => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Zeile 3: Lieferbeginn, Branche */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lieferbeginn</label>
                  <input
                    type="date"
                    value={formData.lieferbeginn}
                    onChange={e => updateForm('lieferbeginn', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Branche</label>
                  <select
                    value={formData.branche}
                    onChange={e => updateForm('branche', e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">Bitte waehlen</option>
                    {BRANCHEN.map(b => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Zeile 4: Kurzbezeichnung */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kurzbezeichnung (Referenz-ID)</label>
                <input
                  type="text"
                  value={formData.kurzbezeichnung}
                  onChange={e => updateForm('kurzbezeichnung', e.target.value)}
                  placeholder="z.B. Kunde Mustermann"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                />
              </div>
            </div>
          </Card>
        </div>

        {/* Rechte Sidebar */}
        <div className="space-y-4">
          {/* Erkannter Netzbetreiber Detail */}
          {detectedNB && (
            <Card>
              <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Netzgebiet</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Netzbetreiber:</span>
                  <span className="font-medium text-right">{detectedNB.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Region:</span>
                  <span className="font-medium">{detectedNB.region}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Netzentgelt (Schaetzwert):</span>
                  {/* Zwei Nachkommastellen: der Wert ist eine Regionalpauschale, keine Tarifangabe */}
                  <span className="font-medium text-amber-700">{detectedNB.netzentgelt.toFixed(2)} ct/kWh</span>
                </div>
              </div>

              {/* Keine Genauigkeit vortaeuschen, die diese Tabelle nicht hergibt */}
              <div className="mt-3 p-2 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900 space-y-1">
                <p className="font-semibold">Regionaler Schaetzwert nach 2-stelliger PLZ</p>
                <p>
                  Dieser Vorschauwert gilt fuer die ganze Region — er ist nicht der Tarif von{' '}
                  {detectedNB.name}. Ist fuer diesen Netzbetreiber ein Preisblatt hinterlegt,
                  rechnet die Kalkulation damit; das Ergebnis weist die Herkunft aus.
                </p>
                {!detectedNB.exact && (
                  <p>
                    Der Netzbetreiber wurde ueber eine benachbarte PLZ zugeordnet — bitte pruefen.
                  </p>
                )}
                <p>
                  <Link to={NETZENTGELTE_PFAD} className="underline font-medium">
                    Preisblatt unter Netzentgelte hinterlegen
                  </Link>
                </p>
              </div>
            </Card>
          )}

          {/* Preisbasis-Umschalter */}
          <Card>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Preisbasis</h3>
            <div className="space-y-2">
              <label className={`flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border-2 transition-all ${priceMode === 'boerse' ? 'border-teal-500 bg-teal-50' : 'border-gray-200'}`}>
                <input
                  type="radio"
                  name="priceMode"
                  checked={priceMode === 'boerse'}
                  onChange={() => setPriceMode('boerse')}
                  className="text-teal-600 mt-0.5"
                />
                <div>
                  <span className="font-medium text-gray-900">Boersenbasiert</span>
                  <p className="text-xs text-gray-500">Live EEX-Futures + Strukturaufschlag</p>
                </div>
              </label>
              <label className={`flex items-start gap-2 text-sm cursor-pointer p-2 rounded-lg border-2 transition-all ${priceMode === 'versorger' ? 'border-teal-500 bg-teal-50' : 'border-gray-200'}`}>
                <input
                  type="radio"
                  name="priceMode"
                  checked={priceMode === 'versorger'}
                  onChange={() => setPriceMode('versorger')}
                  className="text-teal-600 mt-0.5"
                />
                <div>
                  <span className="font-medium text-gray-900">Versorgerpreise</span>
                  <p className="text-xs text-gray-500">Manuell eingetragene Tarife</p>
                </div>
              </label>
            </div>
          </Card>

          {/* EEX-Stand (nur im Boerse-Modus) */}
          {priceMode === 'boerse' && futures?.prices && (
            <Card>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-500 uppercase">EEX Boerse</h3>
                <span className="text-xs text-gray-400">
                  {futures.stand
                    ? `Settlement ${new Date(futures.stand).toLocaleDateString('de-DE')}${futures.veraltet ? ' — VERALTET' : ''}`
                    : 'Handelstag unbekannt'}
                </span>
              </div>
              <div className="space-y-1 text-sm">
                {[new Date().getFullYear() + 1, new Date().getFullYear() + 2, new Date().getFullYear() + 3].map(yr => (
                  futures.prices[String(yr)] != null && (
                    <div key={yr} className="flex justify-between">
                      <span className="text-gray-500">Cal-{yr}</span>
                      <span className="font-medium text-teal-700">{futures.prices[String(yr)].toFixed(3)} ct/kWh</span>
                    </div>
                  )
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-2">Phelix-DE Baseload</p>
            </Card>
          )}

          {/* Berechnung starten */}
          <button
            onClick={onCalculate}
            disabled={!canCalculate || isCalculating}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-center flex items-center justify-center gap-2 transition-all
              ${canCalculate
                ? 'bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 cursor-pointer'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }
            `}
          >
            {isCalculating ? (
              <FiRefreshCw className="animate-spin" size={18} />
            ) : (
              <FiArrowRight size={18} />
            )}
            Berechnung starten
          </button>

          {/* Ehrlicher Fehlerzustand statt still erfundener Preise */}
          {calcError && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {calcError}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Schritt 2: AUFSCHLAG
// ============================================================
const AufschlagStep = ({
  result, provision, onProvisionChange, selectedSuppliers, onToggleSupplier, onNext, onBack
}) => {
  const [showAllAngebote, setShowAllAngebote] = useState(false);

  if (!result) return null;

  const {
    laufzeiten, cheapestPerLaufzeit, netzentgelt_ct_kwh, abgaben_ct_kwh, verbrauch,
    netzentgelt_herkunft, benutzungsstunden, zaehlverfahren, leistung,
  } = result;

  // Nur Daten fuer cheapest pro Laufzeit (wie ESOS24 Hauptansicht)
  const cheapestRows = laufzeiten.map(lz => cheapestPerLaufzeit[lz]).filter(Boolean);

  const stundenAufschlag = benutzungsstunden != null
    ? Number(benutzungsstunden)
    : berechneBenutzungsstunden(verbrauch, leistung);

  // Nicht verhandelbarer Block — wird ausgewiesen, aber nicht eingerechnet.
  const zzglSumme = fmtZzglSumme(netzentgelt_ct_kwh, abgaben_ct_kwh, netzentgelt_herkunft);

  return (
    <div className="max-w-6xl mx-auto">
      {/* Provision einstellen */}
      <Card className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">PROVISION EINSTELLEN</h2>
          <div className="flex items-center gap-4 text-sm">
            <div title={ZZGL_TITLE}>
              <span className="text-gray-500">ZZGL. NETZ + ABGABEN: </span>
              <span className="font-medium text-gray-600">{zzglSumme} ct/kWh</span>
            </div>
            <div>
              <span className="text-gray-500">PROVISION: </span>
              <span className="font-bold text-teal-700">{provision.toFixed(3)} ct/kWh</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-500 w-16">Provision</span>
          <input
            type="range"
            min="0.1"
            max="5.0"
            step="0.1"
            value={provision}
            onChange={e => onProvisionChange(parseFloat(e.target.value))}
            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-teal-600"
          />
          <div className="flex items-center gap-1">
            <input
              type="number"
              min="0.1"
              max="10"
              step="0.1"
              value={provision}
              onChange={e => onProvisionChange(parseFloat(e.target.value) || 0.1)}
              className="w-20 px-2 py-1 border rounded text-right font-mono"
            />
            <span className="text-sm text-gray-500">ct/kWh</span>
          </div>
        </div>
      </Card>

      {/* Info-Leiste */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-2">
          <button
            onClick={() => setShowAllAngebote(false)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${!showAllAngebote ? 'bg-white border-2 border-teal-500 text-teal-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Guenstigstes pro Laufzeit
          </button>
          <button
            onClick={() => setShowAllAngebote(true)}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${showAllAngebote ? 'bg-white border-2 border-teal-500 text-teal-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Alle Angebote ({result.total_suppliers} Versorger)
          </button>
        </div>
        <span className="text-sm text-gray-400">
          {result.total_angebote} Angebote von {result.total_suppliers} Versorgern
        </span>
      </div>

      {/* Vergleichstabelle */}
      <Card className="overflow-hidden">
        {!showAllAngebote ? (
          /* Guenstigstes pro Laufzeit — horizontale Ansicht wie ESOS24 */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600 w-48">
                    {verbrauch?.toLocaleString('de-DE')} kWh<br />
                    <span className="text-xs text-gray-400">1 Lieferstelle</span>
                  </th>
                  {cheapestRows.map((row, idx) => (
                    <th key={idx} className="text-center px-4 py-3 font-medium text-gray-700">
                      <div className="font-semibold">{row.supplier_name}</div>
                      <div className="text-xs text-gray-400">bis {row.lieferende_display}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* ANGEBOTSPREIS — die Kennzahl: Beschaffung + Provision */}
                <tr className="bg-teal-50">
                  <td className="px-4 py-3 align-top">
                    <div className="font-bold text-teal-800">Angebotspreis (Energie)</div>
                    <div className="text-xs text-teal-700 mt-0.5">Beschaffung + Provision — der verhandelbare Teil</div>
                    <div className="text-xs font-semibold text-gray-600 mt-1">{ZZGL_TEXT}</div>
                  </td>
                  {cheapestRows.map((row, idx) => (
                    <td key={idx} className="text-center px-4 py-3 align-top">
                      <div className="text-xl font-bold text-teal-700">
                        {fmtCt(angebotspreisCt(row), 4)}
                        <span className="text-sm font-semibold"> ct/kWh</span>
                      </div>
                      <div className="text-sm font-medium text-teal-600">
                        {fmtEUR(angebotspreisJahr(row, verbrauch))} &euro;/a
                      </div>
                      <div className="text-[10px] leading-tight text-gray-500 mt-1">{ZZGL_TEXT}</div>
                    </td>
                  ))}
                </tr>
                {/* Energiepreis (Beschaffung) — Bestandteil des Angebotspreises */}
                <tr className="border-t">
                  <td className="px-4 py-2 text-gray-600 pl-8 text-xs">davon Beschaffung (Energiepreis)</td>
                  {cheapestRows.map((row, idx) => (
                    <td key={idx} className="text-center px-4 py-2 text-xs">{fmtCt(row.energie_ct_kwh, 4)} ct/kWh</td>
                  ))}
                </tr>
                {/* Boerse-Aufschluesselung (nur im Boerse-Modus) */}
                {result.mode === 'boerse' && (
                  <>
                    <tr className="border-t bg-blue-50/40">
                      <td className="px-4 py-2 text-gray-500 pl-8 text-xs">davon Boerse (EEX)</td>
                      {cheapestRows.map((row, idx) => (
                        <td key={idx} className="text-center px-4 py-2 text-xs text-blue-700">{row.boerse_ct_kwh?.toFixed(4)} ct/kWh</td>
                      ))}
                    </tr>
                    <tr className="border-t bg-blue-50/40">
                      <td className="px-4 py-2 text-gray-500 pl-8 text-xs">Strukturaufschlag</td>
                      {cheapestRows.map((row, idx) => (
                        <td key={idx} className="text-center px-4 py-2 text-xs text-blue-700">+{row.aufschlag_ct_kwh?.toFixed(4)} ct/kWh</td>
                      ))}
                    </tr>
                  </>
                )}
                {/* AIQCON-Provision — zweiter Bestandteil des Angebotspreises */}
                <tr className="border-t">
                  <td className="px-4 py-2 text-gray-600 pl-8 text-xs">davon AIQCON-Provision</td>
                  {cheapestRows.map((row, idx) => (
                    <td key={idx} className="text-center px-4 py-2 text-xs text-amber-700">
                      +{fmtCt(row.provision_ct_kwh, 4)} ct/kWh
                    </td>
                  ))}
                </tr>
                {/* Provision — unveraendert, steht jetzt bewusst OBERHALB des zzgl.-Blocks:
                    die Provision ist Teil des Angebotspreises, kein regulierter Aufschlag. */}
                <tr className="border-t bg-amber-50">
                  <td className="px-4 py-2 font-medium text-amber-700">Provision / Laufzeit</td>
                  {cheapestRows.map((row, idx) => (
                    <td key={idx} className="text-center px-4 py-2 font-medium text-amber-600">
                      {row.provision_jahr_eur?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} &euro;
                    </td>
                  ))}
                </tr>

                {/* ---- Ab hier: reguliert, nicht verhandelbar, NICHT im Angebotspreis ---- */}
                <tr className="border-t-2 border-gray-300 bg-gray-50">
                  <td colSpan={cheapestRows.length + 1} className="px-4 py-2">
                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                      {ZZGL_TEXT}
                    </span>
                    <span className="text-xs text-gray-500 ml-2">
                      — staatlich reguliert, bei jedem Versorger identisch und nicht im Angebotspreis enthalten
                    </span>
                  </td>
                </tr>
                {/* Netzentgelte — mit Herkunfts-Kennzeichnung */}
                <tr className="border-t">
                  <td className="px-4 py-2 text-gray-600">
                    <div className="flex items-center gap-2">
                      <ZzglBadge />
                      <span>Netzentgelte</span>
                      <HerkunftBadge herkunft={netzentgelt_herkunft} />
                    </div>
                    {zaehlverfahren === 'RLM' && (
                      <div className="text-xs text-gray-400 mt-0.5">
                        {Number(leistung) > 0
                          ? `Benutzungsstunden: ${formatStunden(stundenAufschlag)}`
                          : 'ohne Jahreshoechstleistung — kein Leistungspreis gerechnet'}
                      </div>
                    )}
                  </td>
                  {cheapestRows.map((_, idx) => (
                    <td key={idx} className="text-center px-4 py-2 text-gray-600">
                      {formatNetzentgelt(netzentgelt_ct_kwh, netzentgelt_herkunft)} ct/kWh
                    </td>
                  ))}
                </tr>
                {/* Abgaben und Umlagen */}
                <tr className="border-t">
                  <td className="px-4 py-2 text-gray-600">
                    <div className="flex items-center gap-2">
                      <ZzglBadge />
                      <span>Abgaben und Umlagen</span>
                    </div>
                  </td>
                  {cheapestRows.map((_, idx) => (
                    <td key={idx} className="text-center px-4 py-2 text-gray-600">{fmtCt(abgaben_ct_kwh, 4)} ct/kWh</td>
                  ))}
                </tr>
                {/* Nachrangig: geschaetzte Gesamtkosten — NICHT der Angebotspreis */}
                <tr className="border-t bg-gray-50/60">
                  <td className="px-4 py-2 align-top">
                    <div className="text-xs font-medium text-gray-500">
                      Geschaetzte Gesamtkosten inkl. Netz und Abgaben
                    </div>
                    <GesamtkostenHinweis herkunft={netzentgelt_herkunft} className="mt-0.5 max-w-xs" />
                  </td>
                  {cheapestRows.map((row, idx) => (
                    <td key={idx} className="text-center px-4 py-2 align-top">
                      <div className="text-xs text-gray-500">
                        {fmtCt(row.komplettpreis_ct_kwh, istPreisblatt(netzentgelt_herkunft) ? 4 : 2)} ct/kWh
                      </div>
                      <div className="text-xs text-gray-400">
                        {fmtEUR(row.komplettpreis_jahr_eur)} &euro;/a
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        ) : (
          /* Alle Angebote — vertikale Liste nach Laufzeit */
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-amber-50">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Versorger</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Lieferbeginn</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Lieferende</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Monate</th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">
                    Angebotspreis (Energie)
                    <div className="text-[10px] font-normal text-gray-500">{ZZGL_TEXT}</div>
                  </th>
                  <th className="text-center px-4 py-3 font-medium text-gray-600">
                    Angebotspreis/a
                    <div className="text-[10px] font-normal text-gray-500">{ZZGL_TEXT}</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {laufzeiten.map(lz => {
                  const supplierResults = result.results
                    .filter(r => r.laufzeiten.some(l => l.laufzeit_monate === lz))
                    .map(r => ({
                      ...r,
                      laufzeit: r.laufzeiten.find(l => l.laufzeit_monate === lz),
                    }))
                    .sort((a, b) => a.laufzeit.energie_ct_kwh - b.laufzeit.energie_ct_kwh);

                  const cheapest = cheapestPerLaufzeit[lz];

                  return (
                    <React.Fragment key={lz}>
                      <tr className="bg-gray-100">
                        <td colSpan={6} className="px-4 py-2 font-semibold text-gray-600">
                          Laufzeit {lz / 12 > 0 ? `${Math.round(lz / 12)} Jahr${lz > 12 ? 'e' : ''}` : `${lz} Monate`}
                        </td>
                      </tr>
                      {supplierResults.map((sr, idx) => {
                        const isCheapest = cheapest && sr.laufzeit.energie_ct_kwh === cheapest.energie_ct_kwh;
                        return (
                          <tr key={`${lz}-${sr.supplier_id}`} className={`border-t ${isCheapest ? 'bg-teal-50' : 'hover:bg-gray-50'}`}>
                            <td className="px-4 py-2 font-medium">{sr.supplier_name}</td>
                            <td className="text-center px-4 py-2">{result.lieferbeginn}</td>
                            <td className="text-center px-4 py-2">bis {sr.laufzeit.lieferende_display}</td>
                            <td className="text-center px-4 py-2">{sr.laufzeit.laufzeit_monate}</td>
                            <td className="text-center px-4 py-2">{fmtCt(angebotspreisCt(sr.laufzeit), 4)}</td>
                            <td className="text-center px-4 py-2 font-medium">
                              {fmtEUR(angebotspreisJahr(sr.laufzeit, verbrauch))} &euro;
                            </td>
                          </tr>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Woher das Netzentgelt stammt — gilt fuer beide Tabellen-Ansichten */}
      <NetzentgeltHerkunft result={result} className="mt-4" />

      {/* Info-Text */}
      <p className="text-xs text-gray-500 mt-4 text-center">
        Angeboten wird der <strong>Angebotspreis (Energie)</strong> = Beschaffung + Provision,
        {' '}<strong>{ZZGL_TEXT}</strong>. Netzentgelte, Stromsteuer, KWKG-, Offshore- und
        §19-Umlage sowie die Konzessionsabgabe sind bei jedem Versorger identisch und nicht verhandelbar.
      </p>
      <p className="text-xs text-gray-400 mt-2 text-center">
        Dargestellt ist das GUENSTIGSTE ANGEBOT PRO LAUFZEIT. Alle verfuegbaren Angebote sind unter dem Tab &quot;Alle Angebote&quot; aufgefuehrt.
      </p>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button variant="secondary" onClick={onBack}>
          <FiArrowLeft size={16} className="mr-1" /> Zurueck
        </Button>
        <button
          onClick={onNext}
          className="px-6 py-3 bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 rounded-lg font-semibold flex items-center gap-2 transition-all"
        >
          Zur Zusammenfassung <FiArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

// ============================================================
// Schritt 3: ZUSAMMENFASSUNG
// ============================================================
const ZusammenfassungStep = ({
  result, provision, filteredResults, onSave, onBack, savedKalkulation, customerPrefill,
  isSaving, saveError
}) => {
  const [viewMode, setViewMode] = useState('detailliert');
  const [activeLaufzeit, setActiveLaufzeit] = useState(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [shareSlug, setShareSlug] = useState('');
  const [shareForm, setShareForm] = useState({
    contact_name: '', company_name: '', contact_phone: '', contact_email: '', personal_message: '', valid_days: 14,
  });
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareSaving, setShareSaving] = useState(false);
  const [shareError, setShareError] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');
  const [ghlSending, setGhlSending] = useState(false);
  const [ghlStatus, setGhlStatus] = useState(null);

  // Kundendaten aus Lead vorausfuellen
  useEffect(() => {
    if (customerPrefill) {
      setShareForm(prev => ({
        ...prev,
        contact_name: customerPrefill.contact_name || prev.contact_name,
        company_name: customerPrefill.company_name || prev.company_name,
        contact_phone: customerPrefill.contact_phone || prev.contact_phone,
        contact_email: customerPrefill.contact_email || prev.contact_email,
      }));
    }
  }, [customerPrefill]);

  /**
   * Legt das Angebot SERVERSEITIG an und gibt den Link erst zurueck, wenn der
   * Server die Speicherung bestaetigt hat. Der Vertriebler darf nie einen Link
   * in die Hand bekommen, der beim Kunden ins Leere laeuft.
   *
   * @returns {Promise<{ ok: true, slug: string, link: string } | { ok: false, error: string }>}
   */
  const generateShareLink = async () => {
    if (shareSaving) return { ok: false, error: 'Speichern laeuft bereits' };
    setShareSaving(true);
    setShareError('');

    // Slug ohne mehrdeutige Zeichen (kein O/0, I/1, etc.)
    const SLUG_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    const slug = Array.from({ length: 8 }, () =>
      SLUG_CHARS[Math.floor(Math.random() * SLUG_CHARS.length)]
    ).join('');
    const sharedData = {
      slug,
      ...result,
      // Preisbasis: der Kunde sieht den Angebotspreis (Energie + Provision).
      // Netzentgelte, Abgaben und Umlagen sind darin NICHT enthalten, werden aber
      // weiterhin mitgeliefert, damit die Kundenansicht sie ausweisen kann.
      // Alte Schnappschuesse tragen dieses Feld nicht — die Kundenansicht
      // erkennt daran, dass komplettpreis dort noch DER Preis war.
      preisbasis: 'energie',
      // angebotspreis_* kommt vom Rechenkern; die Anreicherung stellt sicher, dass
      // die Felder auch bei einer geladenen Alt-Kalkulation im Schnappschuss stehen.
      results: (result.results || []).map((r) => ({
        ...r,
        laufzeiten: (r.laufzeiten || []).map((lz) => mitAngebotspreis(lz, result.verbrauch)),
      })),
      cheapestPerLaufzeit: Object.fromEntries(
        Object.entries(result.cheapestPerLaufzeit || {}).map(([lz, data]) => [
          lz,
          mitAngebotspreis(data, result.verbrauch),
        ])
      ),
      // Kundeninfos
      contact_name: shareForm.contact_name,
      company_name: shareForm.company_name,
      contact_phone: shareForm.contact_phone,
      contact_email: shareForm.contact_email,
      // Anschrift der Verbrauchsstelle: der Kunde soll sie im Vollmacht-Formular
      // nicht abtippen muessen. Wird vom Server an die Kundenansicht ausgeliefert.
      strasse: customerPrefill?.strasse || '',
      ort: customerPrefill?.ort || '',
      kunde_plz: customerPrefill?.kunde_plz || '',
      lead_id: customerPrefill?.lead_id || null,
      ghl_contact_id: customerPrefill?.ghl_contact_id || null,
      current_price_ct_kwh: customerPrefill?.current_price_ct_kwh || null,
      current_supplier: customerPrefill?.current_supplier || '',
      personal_message: shareForm.personal_message,
      valid_until: shareForm.valid_days > 0
        ? new Date(Date.now() + shareForm.valid_days * 86400000).toISOString().slice(0, 10)
        : null,
      // Meta
      created_at: new Date().toISOString(),
      status: 'gesendet',
      views: 0,
      // Provision ist in den Preisen eingerechnet, aber nicht separat sichtbar
    };

    try {
      const resp = await apiFetch('/api/shared-kalkulationen', {
        method: 'POST',
        body: JSON.stringify({
          slug,
          data: sharedData,
          contact_name: shareForm.contact_name,
          company_name: shareForm.company_name,
          valid_until: sharedData.valid_until,
        }),
      });

      if (!resp.ok) {
        let message = `Angebot konnte nicht gespeichert werden (HTTP ${resp.status})`;
        try {
          const data = await resp.json();
          if (data?.error) message = 'Angebot konnte nicht gespeichert werden: ' + data.error;
        } catch {
          // Antwort war kein JSON — HTTP-Status bleibt die Meldung.
        }
        setShareError(message);
        return { ok: false, error: message };
      }

      // Erst JETZT existiert das Angebot auf dem Server — vorher gibt es keinen Link.
      const link = `${window.location.origin}/k/${slug}`;
      setShareSlug(slug);
      setShareLink(link);
      return { ok: true, slug, link };
    } catch (err) {
      const message = 'Server nicht erreichbar — Angebot wurde NICHT gespeichert: ' + err.message;
      setShareError(message);
      return { ok: false, error: message };
    } finally {
      setShareSaving(false);
    }
  };

  // Preisspanne fuer GHL/Anzeige — ausgewiesen wird der ANGEBOTSPREIS (Energie +
  // Provision). Der Zusatz "zzgl. ..." gehoert zwingend dazu: ohne ihn koennte der
  // Kunde die niedrigere Zahl fuer seinen Gesamtpreis halten.
  const getPreisspanne = () => {
    const prices = (result.laufzeiten || [])
      .map((lz) => angebotspreisCt(result.cheapestPerLaufzeit?.[lz]))
      .filter((p) => typeof p === 'number' && !Number.isNaN(p));
    if (!prices.length) return '';
    const fmt = (n) => n.toFixed(2).replace('.', ',');
    const min = Math.min(...prices), max = Math.max(...prices);
    const spanne = min === max ? fmt(min) : `${fmt(min)} – ${fmt(max)}`;
    return `${spanne} ct/kWh Energiepreis (${ZZGL_TEXT})`;
  };

  // Explizite GHL-Rueckschreibung: schreibt Angebotsdaten in den Kontakt und
  // verschiebt die Opportunity -> GHL-Workflow versendet die Email.
  // Nur per ausdruecklichem Button (loest Kunden-Email aus!).
  const sendViaGhl = async () => {
    const ghlContactId = customerPrefill?.ghl_contact_id;
    if (!ghlContactId) return;
    setGhlSending(true);
    setGhlStatus(null);
    try {
      let slug = shareSlug;
      let link = shareLink;
      // Ohne bestaetigt gespeichertes Angebot darf nichts an GHL gehen —
      // sonst versendet der Workflow eine Mail mit totem Link.
      if (!slug || !link) {
        const created = await generateShareLink();
        if (!created.ok) {
          setGhlStatus({ ok: false, msg: created.error });
          return;
        }
        slug = created.slug;
        link = created.link;
      }
      const resp = await apiFetch('/api/ghl/offer-sent', {
        method: 'POST',
        body: JSON.stringify({ contactId: ghlContactId, slug, link, company: shareForm.company_name, preisspanne: getPreisspanne() }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        setGhlStatus({ ok: false, msg: data.error || 'GHL-Update fehlgeschlagen' });
      } else if (data.skipped) {
        setGhlStatus({ ok: false, msg: 'GHL ist serverseitig noch nicht konfiguriert (Token fehlt).' });
      } else {
        const moved = data.opportunity?.moved;
        const created = data.opportunity?.created;
        setGhlStatus({
          ok: true,
          msg: moved
            ? (created
                ? 'An Kunde gesendet — Kontakt aktualisiert & neue Opportunity auf „Angebot gesendet" angelegt. GHL versendet die E-Mail.'
                : 'An Kunde gesendet — Kontakt aktualisiert & Opportunity verschoben. GHL versendet die E-Mail.')
            : `Kontakt aktualisiert. Hinweis: Opportunity nicht gesetzt (${data.opportunity?.reason || 'unbekannt'}).`,
          warn: !moved,
        });
      }
    } catch (e) {
      setGhlStatus({ ok: false, msg: 'Fehler: ' + e.message });
    } finally {
      setGhlSending(false);
    }
  };

  const handleShare = async () => {
    setShowShareDialog(true);
    // Der Link erscheint erst, wenn generateShareLink erfolgreich war.
    // Bei Fehler bleibt das Formular stehen und zeigt shareError an.
    await generateShareLink();
  };

  // Versorger-Anfrage
  const [showAnfrageDialog, setShowAnfrageDialog] = useState(false);
  const [anfrageForm, setAnfrageForm] = useState({
    medium: 'strom', angebotstyp: 'fest', vertriebspartner_nr: '', malo_id: '',
    firma: '', branche: '', lieferadresse: '', hoechstleistung: '', bemerkungen: '',
  });
  // Ausschreibung aus der Versorger-Anfrage heraus anlegen (Standard: aktiv)
  const [alsAusschreibung, setAlsAusschreibung] = useState(true);
  const [anfrageBusy, setAnfrageBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const [ausschreibung, setAusschreibung] = useState(null);
  const [ausschreibungError, setAusschreibungError] = useState('');

  // Dialog oeffnen und den Firmennamen aus den bereits bekannten Kundendaten
  // vorbelegen — company_name ist fuer die Ausschreibung ein Pflichtfeld.
  const openAnfrageDialog = () => {
    setPdfError('');
    setAusschreibungError('');
    // ACHTUNG: die bereits angelegte Ausschreibung NICHT zuruecksetzen.
    // Sonst greift die Doppelanlage-Sperre nur innerhalb einer Dialog-Sitzung,
    // und ein zweiter Klick nach dem Schliessen erzeugt eine zweite
    // Ausschreibung fuer denselben Kunden.
    setAnfrageForm(p => ({
      ...p,
      firma: p.firma || shareForm.company_name || customerPrefill?.company_name || '',
    }));
    setShowAnfrageDialog(true);
  };

  /**
   * Erzeugt das Anfrage-PDF und legt — falls gewuenscht — zusaetzlich eine
   * Ausschreibung an, damit Versorger-Rueckläufe und Provision verfolgbar sind.
   *
   * Reihenfolge ist bewusst: Das PDF wird IMMER erzeugt, auch wenn das Anlegen
   * der Ausschreibung anschliessend scheitert. Beide Fehler werden sichtbar
   * gemeldet, nie stillschweigend geschluckt.
   */
  const handleAnfrage = async () => {
    if (anfrageBusy) return;
    setAnfrageBusy(true);
    setPdfError('');
    setAusschreibungError('');

    const firma = (anfrageForm.firma || shareForm.company_name || customerPrefill?.company_name || '').trim();
    const kontakt = (shareForm.contact_name || customerPrefill?.contact_name || '').trim();
    // Deutsche Schreibweise korrekt lesen: "1.250" sind 1250 kW, nicht 1,25.
    // parseFloat('1.250') haette still 1.25 ergeben — Faktor 1000 daneben.
    const num = (v) => {
      let s = String(v ?? '').trim();
      if (s === '') return null;
      s = s.replace(/[^0-9,.\-]/g, '');
      if (s.includes(',')) s = s.replace(/\./g, '').replace(',', '.'); // 1.250,5 -> 1250.5
      else if (/^-?\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, ''); // 1.250 -> 1250
      const n = parseFloat(s);
      return Number.isFinite(n) ? n : null;
    };
    // Versorger werden in Standard-Laufzeiten angefragt (12/24/36/48/60),
    // nicht in der kalendarischen Restlaufzeit bis zum 31.12.
    const laufzeitenMonate = Object.values(result.cheapestPerLaufzeit || {})
      .map((l) => (l.laufzeit_jahre ? l.laufzeit_jahre * 12 : l.laufzeit_monate))
      .filter(Boolean);

    const payload = {
      medium: anfrageForm.medium,
      vertriebspartner_nr: anfrageForm.vertriebspartner_nr,
      firma,
      branche: anfrageForm.branche,
      lieferadresse: anfrageForm.lieferadresse || `PLZ ${result.plz}`,
      rlm: result.zaehlverfahren === 'RLM',
      angebotstyp: anfrageForm.angebotstyp,
      buendel: false,
      verbrauch: result.verbrauch,
      hoechstleistung: anfrageForm.hoechstleistung || null,
      lieferspannung: result.spannungsebene || 'NS',
      lieferzeitraum: laufzeitenMonate,
      lieferbeginn: result.lieferbeginn,
      malo_id: anfrageForm.malo_id,
      aufschlag_ct: provision,
      bemerkungen: anfrageForm.bemerkungen,
    };

    // 1. PDF — hat Vorrang, laeuft unabhaengig von der Ausschreibung
    try {
      const resp = await apiFetch('/api/angebotsanfrage-pdf', { method: 'POST', body: JSON.stringify(payload) });
      if (!resp.ok) {
        let message = `Anfrage-PDF konnte nicht erzeugt werden (HTTP ${resp.status})`;
        try {
          const data = await resp.json();
          if (data?.error) message = 'Anfrage-PDF konnte nicht erzeugt werden: ' + data.error;
        } catch {
          // Antwort war kein JSON — HTTP-Status bleibt die Meldung.
        }
        setPdfError(message);
      } else {
        const blob = await resp.blob();
        window.open(URL.createObjectURL(blob), '_blank');
      }
    } catch (e) {
      setPdfError('Anfrage-PDF konnte nicht erzeugt werden: ' + e.message);
    }

    // 2. Ausschreibung — nur wenn gewuenscht und noch keine angelegt wurde
    if (alsAusschreibung && !ausschreibung) {
      if (!firma) {
        setAusschreibungError('Ausschreibung nicht angelegt: Bitte einen Firmennamen eintragen.');
      } else {
        try {
          const resp = await apiFetch('/api/ausschreibungen', {
            method: 'POST',
            body: JSON.stringify({
              company_name: firma,
              contact_name: kontakt || null,
              medium: anfrageForm.medium,
              plz: result.plz || null,
              verbrauch_kwh: result.verbrauch ?? null,
              zaehlverfahren: result.zaehlverfahren || null,
              hoechstleistung_kw: num(anfrageForm.hoechstleistung),
              spannungsebene: result.spannungsebene || null,
              lieferbeginn: result.lieferbeginn || null,
              laufzeiten: laufzeitenMonate,
              angebotstyp: anfrageForm.angebotstyp,
              malo_id: anfrageForm.malo_id || null,
              ziel_provision_ct_kwh: provision ?? null,
              // Eingefrorene Preisbestandteile: die oeffentliche Vergleichsseite
              // (/v/:token) laeuft ohne Login und kann die Jahreskosten nur aus
              // diesen gespeicherten Werten rechnen. Fehlen sie, steht dort dauerhaft "–".
              netzentgelt_ct_kwh: Number.isFinite(result.netzentgelt_ct_kwh) ? result.netzentgelt_ct_kwh : null,
              abgaben_ct_kwh: Number.isFinite(result.abgaben_ct_kwh) ? result.abgaben_ct_kwh : null,
              share_slug: shareSlug || null,
              lead_id: customerPrefill?.lead_id || null,
              kalkulation_id: savedKalkulation?.id || null,
              notizen: anfrageForm.bemerkungen || null,
            }),
          });
          if (!resp.ok) {
            let message = `Ausschreibung konnte nicht angelegt werden (HTTP ${resp.status})`;
            try {
              const data = await resp.json();
              if (data?.error) message = 'Ausschreibung konnte nicht angelegt werden: ' + data.error;
            } catch {
              // Antwort war kein JSON — HTTP-Status bleibt die Meldung.
            }
            setAusschreibungError(message);
          } else {
            // Erfolg erst melden, wenn der Server die Zeile bestaetigt hat.
            const data = await resp.json();
            if (data?.id) setAusschreibung(data);
            else setAusschreibungError('Ausschreibung wurde angelegt, aber der Server hat keine ID zurueckgegeben.');
          }
        } catch (e) {
          setAusschreibungError('Server nicht erreichbar — Ausschreibung wurde NICHT angelegt: ' + e.message);
        }
      }
    }

    setAnfrageBusy(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    });
  };

  if (!result) return null;

  const {
    laufzeiten, cheapestPerLaufzeit, netzentgelt_ct_kwh, abgaben_ct_kwh, verbrauch,
    netzentgelt_herkunft,
  } = result;

  // Setze initiale aktive Laufzeit
  const currentLaufzeit = activeLaufzeit || laufzeiten[0];

  return (
    <div className="max-w-6xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Hauptinhalt */}
        <div className="lg:col-span-3">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                GUENSTIGSTES ANGEBOT PRO LAUFZEIT
              </h2>
              <p className="text-xs text-gray-500 mt-0.5">
                Ausgewiesen wird der <strong>Angebotspreis (Energie)</strong> = Beschaffung + Provision —
                {' '}<strong>{ZZGL_TEXT}</strong>.
              </p>
            </div>
            {savedKalkulation && (
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
                <FiCheck size={14} className="inline mr-1" />
                {savedKalkulation.kalkulation_number}
              </span>
            )}
          </div>

          {/* Versorger-Karten */}
          <div className="space-y-6">
            {laufzeiten.filter(lz => !activeLaufzeit || lz === activeLaufzeit).map(lz => {
              const cheapest = cheapestPerLaufzeit[lz];
              if (!cheapest) return null;

              // Alle Angebote fuer diese Laufzeit
              const allForLaufzeit = filteredResults
                .map(r => ({
                  supplier: r,
                  laufzeit: r.laufzeiten.find(l => l.laufzeit_monate === lz),
                }))
                .filter(x => x.laufzeit)
                .sort((a, b) => a.laufzeit.energie_ct_kwh - b.laufzeit.energie_ct_kwh);

              return (
                <div key={lz}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-sm font-semibold text-gray-500">bis 31.12.{cheapest.lieferende_display?.split('.')[2]}</h3>
                  </div>

                  {allForLaufzeit.map(({ supplier: sr, laufzeit: lzData }, idx) => (
                    <Card key={sr.supplier_id} className={`mb-3 ${idx === 0 ? 'ring-2 ring-teal-200' : ''}`}>
                      <div className="flex items-start gap-4">
                        {/* Versorger-Avatar */}
                        <div className="w-12 h-12 rounded-lg bg-teal-100 flex items-center justify-center font-bold text-teal-700 text-lg flex-shrink-0">
                          {sr.supplier_name?.charAt(0)}
                        </div>

                        {/* Inhalt */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <div>
                              <h4 className="font-bold text-gray-900">{sr.supplier_name}</h4>
                              <p className="text-xs text-gray-500">{lzData.price_type === 'fixed' ? 'Festpreis' : 'Indexiert'}</p>
                            </div>
                            {idx === 0 && (
                              <span className="px-2 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded">
                                Guenstigster
                              </span>
                            )}
                          </div>

                          {viewMode === 'detailliert' ? (
                            <>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
                              <div>
                                <p className="text-xs text-gray-400">Preisdetails Netto</p>
                                <div className="space-y-1 mt-1 text-sm">
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">ø Energie</span>
                                    <span>{fmtCt(lzData.energie_ct_kwh, 3)} ct/kWh</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500">+ Provision</span>
                                    <span className="text-amber-600">{fmtCt(lzData.provision_ct_kwh, 3)} ct/kWh</span>
                                  </div>
                                  <div className="flex justify-between border-t pt-1 font-bold">
                                    <span className="text-teal-800">Angebotspreis</span>
                                    <span className="text-teal-700">{fmtCt(angebotspreisCt(lzData), 3)} ct/kWh</span>
                                  </div>
                                  {/* Ab hier: ausgewiesen, aber NICHT im Angebotspreis */}
                                  <div className="flex justify-between pt-1 border-t border-dashed">
                                    <span className="text-gray-500 flex items-center gap-1">
                                      <ZzglBadge />
                                      ø Netz
                                      <HerkunftBadge herkunft={netzentgelt_herkunft} />
                                    </span>
                                    <span className="text-gray-500">{formatNetzentgelt(netzentgelt_ct_kwh, netzentgelt_herkunft, 3)} ct/kWh</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-gray-500 flex items-center gap-1">
                                      <ZzglBadge />
                                      ø Abgaben
                                    </span>
                                    <span className="text-gray-500">{fmtCt(abgaben_ct_kwh, 3)} ct/kWh</span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex flex-col items-center justify-center text-center">
                                <p className="text-xs text-gray-400 mb-1">ANGEBOTSPREIS (ENERGIE)</p>
                                <p className="text-2xl font-bold text-teal-600">{fmtCt(angebotspreisCt(lzData), 3)}</p>
                                <p className="text-sm text-gray-500">ct/kWh</p>
                                <p className="text-lg font-bold text-gray-900 mt-1">
                                  {fmtEUR(angebotspreisJahr(lzData, verbrauch))} &euro;/a
                                </p>
                                <p className="text-[11px] leading-tight font-semibold text-gray-600 mt-1">
                                  {ZZGL_TEXT}
                                </p>
                              </div>

                              <div className="flex flex-col items-center justify-center">
                                <p className="text-xs text-gray-400 mb-1">Provision</p>
                                <p className="text-lg font-bold text-amber-600">
                                  {lzData.provision_jahr_eur?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} &euro;
                                </p>
                                <p className="text-xs text-gray-400">({provision.toFixed(2)} ct/kWh)</p>
                              </div>

                              <div className="flex flex-col items-center justify-center">
                                <p className="text-xs text-gray-400 mb-1">Laufzeit</p>
                                <p className="text-lg font-bold text-gray-700">{lzData.laufzeit_monate} Monate</p>
                                <p className="text-xs text-gray-400">bis {lzData.lieferende_display}</p>
                              </div>
                            </div>

                            {/* Nachrangig und intern: was der Kunde am Ende insgesamt zahlt */}
                            <div className="mt-3 pt-2 border-t border-dashed">
                              <p className="text-xs text-gray-500">
                                Geschaetzte Gesamtkosten inkl. Netz und Abgaben:{' '}
                                <span className="font-medium text-gray-600">
                                  {fmtCt(lzData.komplettpreis_ct_kwh, istPreisblatt(netzentgelt_herkunft) ? 3 : 2)} ct/kWh
                                </span>
                                {' · '}
                                <span className="font-medium text-gray-600">
                                  {fmtEUR(lzData.komplettpreis_jahr_eur)} &euro;/a
                                </span>
                              </p>
                              <GesamtkostenHinweis herkunft={netzentgelt_herkunft} className="mt-0.5" />
                            </div>
                            </>
                          ) : (
                            <div className="mt-2">
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-bold text-teal-700">{fmtCt(angebotspreisCt(lzData), 3)} ct/kWh</span>
                                <span className="font-bold text-gray-900">{fmtEUR(angebotspreisJahr(lzData, verbrauch))} &euro;/a</span>
                                <span className="text-sm text-amber-600">{lzData.provision_jahr_eur?.toLocaleString('de-DE', { minimumFractionDigits: 2 })} &euro; Provision</span>
                              </div>
                              <p className="text-[11px] font-semibold text-gray-600 mt-0.5">{ZZGL_TEXT}</p>
                              <p className="text-[11px] text-gray-400">
                                Geschaetzte Gesamtkosten inkl. Netz und Abgaben:{' '}
                                {fmtCt(lzData.komplettpreis_ct_kwh, istPreisblatt(netzentgelt_herkunft) ? 3 : 2)} ct/kWh
                                {' · '}
                                {fmtEUR(lzData.komplettpreis_jahr_eur)} &euro;/a
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </Card>
                  ))}
                </div>
              );
            })}
          </div>
        </div>

        {/* Rechte Sidebar */}
        <div className="space-y-4">
          {/* Kalkulationsdaten */}
          <Card>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Kalkulationsdaten</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">Lieferstellen:</span>
                <span className="font-medium">1</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Verbrauch:</span>
                <span className="font-medium">{verbrauch?.toLocaleString('de-DE')} kWh</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Anbieter:</span>
                <span className="font-medium">{result.total_suppliers}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Lieferbeginn:</span>
                <span className="font-medium">{result.lieferbeginn}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Max. Laufzeit:</span>
                <span className="font-medium">31.12.{new Date().getFullYear() + Math.max(...laufzeiten) / 12}</span>
              </div>
            </div>
          </Card>

          {/* Netzentgelt: Wert + Herkunft an einer Stelle — nicht im Angebotspreis */}
          <Card>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold text-gray-500 uppercase">Netzentgelt</h3>
              <ZzglBadge />
            </div>
            <p className="text-xs text-gray-500 mb-3">
              Nicht im Angebotspreis enthalten: Netzentgelte sind Durchleitungskosten des
              Netzbetreibers — reguliert, bei jedem Versorger gleich und nicht verhandelbar.
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Wert:</span>
                <span className="font-medium">
                  {formatNetzentgelt(netzentgelt_ct_kwh, netzentgelt_herkunft, 3)} ct/kWh
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Herkunft:</span>
                <HerkunftBadge herkunft={netzentgelt_herkunft} />
              </div>
            </div>
            <NetzentgeltHerkunft result={result} className="mt-3" />
          </Card>

          {/* Ansicht */}
          <Card>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Ansicht</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="view"
                  checked={viewMode === 'kompakt'}
                  onChange={() => setViewMode('kompakt')}
                  className="text-teal-600"
                />
                Kompakt
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="view"
                  checked={viewMode === 'detailliert'}
                  onChange={() => setViewMode('detailliert')}
                  className="text-teal-600"
                />
                Detailliert
              </label>
            </div>
          </Card>

          {/* Laufzeiten-Filter */}
          <Card>
            <h3 className="text-sm font-semibold text-gray-500 uppercase mb-3">Laufzeiten Anzeigen:</h3>
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="radio"
                  name="laufzeit-filter"
                  checked={!activeLaufzeit}
                  onChange={() => setActiveLaufzeit(null)}
                  className="text-teal-600"
                />
                Alle Laufzeiten
              </label>
              {laufzeiten.map(lz => {
                const endYear = new Date().getFullYear() + Math.round(lz / 12);
                return (
                  <label key={lz} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="laufzeit-filter"
                      checked={activeLaufzeit === lz}
                      onChange={() => setActiveLaufzeit(lz)}
                      className="text-teal-600"
                    />
                    31.12.{endYear}
                  </label>
                );
              })}
            </div>
          </Card>

          {/* Aktionen */}
          <button
            onClick={onSave}
            disabled={!!savedKalkulation || isSaving}
            className={`w-full py-3 px-4 rounded-lg font-semibold text-center flex items-center justify-center gap-2 transition-all
              ${!savedKalkulation
                ? 'bg-teal-600 hover:bg-teal-700 text-white cursor-pointer disabled:opacity-60'
                : 'bg-green-100 text-green-700 cursor-default'
              }
            `}
          >
            {savedKalkulation ? (
              <>
                <FiCheck size={18} />
                Gespeichert
              </>
            ) : isSaving ? (
              <>
                <FiRefreshCw size={18} className="animate-spin" />
                Speichere ...
              </>
            ) : (
              <>
                <FiSave size={18} />
                Kalkulation speichern
              </>
            )}
          </button>

          {saveError && !savedKalkulation && (
            <p className="text-xs text-red-600 mt-2">{saveError}</p>
          )}

          {/* Angebot teilen */}
          <button
            onClick={() => { setShareError(''); setShowShareDialog(true); }}
            className="w-full py-3 px-4 rounded-lg font-semibold text-center flex items-center justify-center gap-2 transition-all bg-amber-100 hover:bg-amber-200 text-amber-900 border border-amber-300 cursor-pointer mt-3"
          >
            <FiSend size={18} />
            Angebot per Link teilen
          </button>

          {/* Versorger-Anfrage */}
          <button
            onClick={openAnfrageDialog}
            className="w-full py-3 px-4 rounded-lg font-medium text-center flex items-center justify-center gap-2 transition-all bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 cursor-pointer mt-2"
          >
            <FiFileText size={18} />
            Versorger-Anfrage (PDF)
          </button>
        </div>
      </div>

      {/* Versorger-Anfrage Dialog */}
      {showAnfrageDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full my-8 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Angebotsanfrage fuer Versorger</h3>
              <button onClick={() => setShowAnfrageDialog(false)} className="text-gray-400 hover:text-gray-600"><FiX size={20} /></button>
            </div>
            <p className="text-sm text-gray-500 mb-4">Vorausgefuellt aus der Kalkulation. Ergaenzen Sie die Versorger-spezifischen Felder.</p>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Medium</label>
                  <select value={anfrageForm.medium} onChange={e => setAnfrageForm(p => ({ ...p, medium: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="strom">Strom</option>
                    <option value="gas">Gas</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Angebotstyp</label>
                  <select value={anfrageForm.angebotstyp} onChange={e => setAnfrageForm(p => ({ ...p, angebotstyp: e.target.value }))} className="w-full px-3 py-2 border rounded-lg text-sm">
                    <option value="fest">Festpreis</option>
                    <option value="spot">Spotpreis</option>
                    <option value="beide">Beide</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Vertriebspartner-Nr." value={anfrageForm.vertriebspartner_nr} onChange={e => setAnfrageForm(p => ({ ...p, vertriebspartner_nr: e.target.value }))} />
                <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="MaLo-ID" value={anfrageForm.malo_id} onChange={e => setAnfrageForm(p => ({ ...p, malo_id: e.target.value }))} />
              </div>
              <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Firma (Kunde)" value={anfrageForm.firma} onChange={e => setAnfrageForm(p => ({ ...p, firma: e.target.value }))} />
              <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Branche" value={anfrageForm.branche} onChange={e => setAnfrageForm(p => ({ ...p, branche: e.target.value }))} />
              <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Lieferadresse (Entnahmestelle)" value={anfrageForm.lieferadresse} onChange={e => setAnfrageForm(p => ({ ...p, lieferadresse: e.target.value }))} />
              {result.zaehlverfahren === 'RLM' && (
                <input className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Jahreshoechstleistung in kW" value={anfrageForm.hoechstleistung} onChange={e => setAnfrageForm(p => ({ ...p, hoechstleistung: e.target.value }))} />
              )}
              <textarea rows={2} className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Bemerkungen" value={anfrageForm.bemerkungen} onChange={e => setAnfrageForm(p => ({ ...p, bemerkungen: e.target.value }))} />

              {/* Ausschreibung mit anlegen — Standard: aktiv */}
              <label className={`flex items-start gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${alsAusschreibung ? 'border-teal-500 bg-teal-50' : 'border-gray-200'} ${ausschreibung ? 'opacity-60 cursor-default' : ''}`}>
                <input
                  type="checkbox"
                  checked={alsAusschreibung}
                  disabled={!!ausschreibung}
                  onChange={e => setAlsAusschreibung(e.target.checked)}
                  className="mt-0.5 text-teal-600"
                />
                <span>
                  <span className="block text-sm font-medium text-gray-900">
                    Als Ausschreibung erfassen, um Rueckläufe und Provision zu verfolgen
                  </span>
                  <span className="block text-xs text-gray-500 mt-0.5">
                    Legt einen Vorgang unter &quot;Abschluesse&quot; an: Versorger-Antworten erfassen,
                    Gewinner waehlen, Provision berechnen.
                  </span>
                </span>
              </label>
            </div>

            <button
              onClick={handleAnfrage}
              disabled={anfrageBusy}
              className="w-full mt-4 py-3 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center justify-center gap-2"
            >
              {anfrageBusy
                ? <><FiRefreshCw size={18} className="animate-spin" /> Wird erstellt ...</>
                : <><FiFileText size={18} /> {alsAusschreibung && !ausschreibung ? 'Anfrage-PDF erstellen & Ausschreibung anlegen' : 'Anfrage-PDF erstellen'}</>
              }
            </button>

            {/* PDF-Fehler: sichtbar, nicht stillschweigend geschluckt */}
            {pdfError && (
              <div className="mt-3 rounded-lg border border-red-200 bg-red-50 p-3">
                <p className="text-sm text-red-700 font-medium">Kein PDF erstellt</p>
                <p className="text-xs text-red-600 mt-1">{pdfError}</p>
              </div>
            )}

            {/* Ausschreibung angelegt */}
            {ausschreibung && (
              <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50 p-3">
                <p className="text-sm text-teal-800 font-medium flex items-center gap-1">
                  <FiCheck size={15} /> Ausschreibung {ausschreibung.nummer || ''} angelegt
                </p>
                <Link
                  to={`/app/abschluesse/${ausschreibung.id}`}
                  className="mt-2 inline-flex items-center gap-1 text-sm font-semibold text-teal-700 hover:text-teal-900 underline"
                >
                  Rueckläufe erfassen <FiArrowRight size={14} />
                </Link>
              </div>
            )}

            {/* Ausschreibung fehlgeschlagen — das PDF ist trotzdem erstellt */}
            {ausschreibungError && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="text-sm text-amber-800 font-medium">Keine Ausschreibung angelegt</p>
                <p className="text-xs text-amber-700 mt-1">{ausschreibungError}</p>
                <p className="text-xs text-amber-700 mt-1">
                  Das Anfrage-PDF ist davon unabhaengig. Die Ausschreibung kann erneut
                  versucht oder spaeter unter &quot;Abschluesse&quot; von Hand angelegt werden.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Share-Dialog */}
      {showShareDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Angebot per Link teilen</h3>
              <button
                disabled={shareSaving}
                onClick={() => {
                  if (shareSaving) return;
                  setShowShareDialog(false);
                  // Slug und Link gehoeren zusammen — nie einen davon stehen lassen,
                  // sonst zeigt der naechste Durchlauf PDF/GHL des alten Angebots.
                  setShareLink('');
                  setShareSlug('');
                  setShareError('');
                  setGhlStatus(null);
                  setEmailStatus('');
                }}
                className="text-gray-400 hover:text-gray-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <FiX size={20} />
              </button>
            </div>

            {!shareLink ? (
              <div className="space-y-4">
                {/* Was der Kunde bekommt: der Angebotspreis, ausdruecklich ohne Netz/Abgaben */}
                <div className="p-3 rounded-lg bg-teal-50 border border-teal-200 text-xs text-teal-900">
                  <p className="font-semibold">Der Kunde sieht den Angebotspreis (Energie)</p>
                  <p className="mt-1">
                    Beschaffung + Provision, ausgewiesen als <strong>{ZZGL_TEXT}</strong>.
                    Netzentgelte und Abgaben stehen im Angebot getrennt daneben, sind aber nicht
                    eingerechnet — sie sind reguliert und bei jedem Versorger gleich.
                  </p>
                </div>

                {/* Bevor ein Schaetzwert das Haus verlaesst: sagen, dass es einer ist */}
                {!istPreisblatt(netzentgelt_herkunft) && (
                  <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                    <p className="font-semibold">Netzentgelt ist ein regionaler Schaetzwert</p>
                    <p className="mt-1">
                      Diese Kalkulation rechnet fuer {result.netzbetreiber || 'diesen Netzbetreiber'} nicht mit
                      einem Preisblatt, sondern mit einer Regionalpauschale. Der Angebotspreis ist davon
                      nicht betroffen — der Schaetzwert steckt nur in den nachrichtlich ausgewiesenen
                      Netzentgelten und in der Gesamtkosten-Schaetzung.{' '}
                      <Link to={NETZENTGELTE_PFAD} className="underline font-medium">
                        Preisblatt hinterlegen
                      </Link>{' '}
                      und neu rechnen, wenn es genauer sein soll.
                    </p>
                  </div>
                )}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ansprechpartner</label>
                  <input
                    type="text"
                    value={shareForm.contact_name}
                    onChange={e => setShareForm(p => ({ ...p, contact_name: e.target.value }))}
                    placeholder="z.B. Herr Mueller"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Firma</label>
                  <input
                    type="text"
                    value={shareForm.company_name}
                    onChange={e => setShareForm(p => ({ ...p, company_name: e.target.value }))}
                    placeholder="z.B. Mueller GmbH"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefon</label>
                    <input
                      type="tel"
                      value={shareForm.contact_phone}
                      onChange={e => setShareForm(p => ({ ...p, contact_phone: e.target.value }))}
                      placeholder="z.B. 0170 1234567"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">E-Mail</label>
                    <input
                      type="email"
                      value={shareForm.contact_email}
                      onChange={e => setShareForm(p => ({ ...p, contact_email: e.target.value }))}
                      placeholder="kunde@firma.de"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Persoenliche Nachricht (optional)</label>
                  <textarea
                    value={shareForm.personal_message}
                    onChange={e => setShareForm(p => ({ ...p, personal_message: e.target.value }))}
                    placeholder="z.B. Hier Ihr individuelles Stromangebot..."
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gueltigkeit (Tage)</label>
                  <input
                    type="number"
                    value={shareForm.valid_days}
                    onChange={e => setShareForm(p => ({ ...p, valid_days: parseInt(e.target.value) || 0 }))}
                    min="0"
                    max="90"
                    className="w-24 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-teal-500"
                  />
                </div>

                {shareError && (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-700 font-medium">Kein Link erstellt</p>
                    <p className="text-xs text-red-600 mt-1">{shareError}</p>
                    <p className="text-xs text-red-600 mt-1">
                      Bitte erneut versuchen. Solange kein Link erscheint, gibt es das Angebot
                      auch beim Kunden nicht.
                    </p>
                  </div>
                )}

                <button
                  onClick={handleShare}
                  disabled={shareSaving}
                  className="w-full py-3 bg-teal-600 hover:bg-teal-700 disabled:opacity-60 disabled:cursor-not-allowed text-white rounded-lg font-semibold flex items-center justify-center gap-2"
                >
                  {shareSaving ? <FiRefreshCw size={18} className="animate-spin" /> : <FiLink size={18} />}
                  {shareSaving ? 'Angebot wird gespeichert ...' : shareError ? 'Erneut versuchen' : 'Link generieren'}
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <FiCheck className="mx-auto text-green-600 mb-2" size={24} />
                  <p className="text-sm text-green-700 font-medium">Angebot auf dem Server gespeichert</p>
                  <p className="text-xs text-green-600 mt-1">Der Link ist jetzt fuer den Kunden abrufbar.</p>
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={shareLink}
                    readOnly
                    className="flex-1 px-3 py-2 border rounded-lg bg-gray-50 text-sm font-mono"
                  />
                  <button
                    onClick={copyLink}
                    className={`px-4 py-2 rounded-lg font-medium text-sm flex items-center gap-1 transition-all ${
                      linkCopied
                        ? 'bg-green-100 text-green-700'
                        : 'bg-teal-600 hover:bg-teal-700 text-white'
                    }`}
                  >
                    {linkCopied ? <><FiCheck size={14} /> Kopiert!</> : <><FiCopy size={14} /> Kopieren</>}
                  </button>
                </div>

                <div className="text-sm text-gray-500">
                  <p>Senden Sie diesen Link an Ihren Kunden per:</p>
                  <div className="flex gap-2 mt-2">
                    <a
                      href={`mailto:?subject=Ihr Stromangebot von AIQCON Energy&body=Guten Tag${shareForm.contact_name ? ' ' + shareForm.contact_name : ''},%0A%0Ahier finden Sie Ihr persoenliches Stromangebot:%0A%0A${encodeURIComponent(shareLink)}%0A%0AMit freundlichen Gruessen%0AAIQCON Energy`}
                      className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                    >
                      <FiMail size={14} /> E-Mail
                    </a>
                    <button
                      onClick={() => { if (navigator.share) navigator.share({ title: 'Stromangebot', url: shareLink }); }}
                      className="flex items-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700"
                    >
                      <FiSend size={14} /> Teilen
                    </button>
                  </div>
                </div>

                {/* PDF-Vorschau */}
                <a
                  href={`/api/shared-kalkulationen/${shareSlug}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 rounded-lg text-sm text-gray-700"
                >
                  <FiFileText size={14} /> Angebots-PDF ansehen
                </a>

                {/* An Kunde senden ueber GHL (loest Kunden-Email via GHL-Workflow aus) */}
                {customerPrefill?.ghl_contact_id && (
                  <div className="border-t pt-4">
                    <p className="text-sm font-medium text-gray-700 mb-1">An Kunde senden (über GHL)</p>
                    <p className="text-xs text-gray-500 mb-2">
                      Schreibt das Angebot in den GHL-Kontakt und verschiebt die Opportunity auf „Angebot gesendet" —
                      euer GHL-Workflow versendet die E-Mail. <strong>Löst die Kunden-E-Mail aus.</strong>
                    </p>
                    <button
                      disabled={ghlSending}
                      onClick={sendViaGhl}
                      className="w-full px-4 py-2.5 rounded-lg font-medium text-sm flex items-center justify-center gap-2 bg-teal-600 hover:bg-teal-700 text-white disabled:opacity-60"
                    >
                      <FiSend size={15} /> {ghlSending ? 'Sende an GHL …' : 'An Kunde senden (über GHL)'}
                    </button>
                    {ghlStatus && (
                      <p className={`text-xs mt-2 ${ghlStatus.ok ? (ghlStatus.warn ? 'text-amber-600' : 'text-green-600') : 'text-red-600'}`}>
                        {ghlStatus.msg}
                      </p>
                    )}
                  </div>
                )}

                {/* Direkt per Email senden (mit PDF-Anhang) */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Per E-Mail senden (mit PDF-Anhang):</p>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      placeholder="kunde@firma.de"
                      id="share-email-input"
                      defaultValue={shareForm.contact_email}
                      className="flex-1 px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
                    />
                    <button
                      disabled={emailSending}
                      onClick={async () => {
                        const emailTo = document.getElementById('share-email-input').value;
                        if (!emailTo) { setEmailStatus('Bitte E-Mail-Adresse eingeben'); return; }
                        setEmailSending(true);
                        setEmailStatus('');
                        try {
                          const resp = await apiFetch('/api/send-offer-email', {
                            method: 'POST',
                            body: JSON.stringify({
                              to: emailTo,
                              offerLink: shareLink,
                              slug: shareSlug,
                              contactName: shareForm.contact_name,
                              companyName: shareForm.company_name,
                              personalMessage: shareForm.personal_message,
                            }),
                          });
                          const data = await resp.json();
                          setEmailStatus(data.success ? `✓ E-Mail mit PDF an ${emailTo} gesendet` : 'Fehler: ' + (data.error || 'Unbekannt'));
                        } catch (e) {
                          setEmailStatus('Fehler beim Senden: ' + e.message);
                        } finally {
                          setEmailSending(false);
                        }
                      }}
                      className="px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-sm font-medium flex items-center gap-1"
                    >
                      {emailSending ? <FiRefreshCw size={14} className="animate-spin" /> : <FiSend size={14} />}
                      {emailSending ? 'Senden...' : 'Senden'}
                    </button>
                  </div>
                  {emailStatus && (
                    <p className={`text-xs mt-2 ${emailStatus.startsWith('✓') ? 'text-green-600' : 'text-red-500'}`}>{emailStatus}</p>
                  )}
                </div>

                <p className="text-xs text-gray-400 text-center">
                  {shareForm.valid_days > 0 && `Gueltig fuer ${shareForm.valid_days} Tage. `}
                  Der Kunde erhaelt Angebots-PDF + Link, ohne Ihre Provision.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <Button variant="secondary" onClick={onBack}>
          <FiArrowLeft size={16} className="mr-1" /> Zurueck
        </Button>
      </div>
    </div>
  );
};

// ============================================================
// Gespeicherte Kalkulationen Bar
// ============================================================
const SavedKalkulationenBar = ({ kalkulationen, onLoad, onDelete, loading, error, onReload }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Ohne Daten nur einblenden, wenn es etwas Ehrliches zu melden gibt
  // (Fehler wird angezeigt, das stille Laden nicht)
  if (kalkulationen.length === 0 && !error) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-gray-800 text-white px-6 py-2 flex items-center gap-2 text-sm"
      >
        {isOpen ? <FiChevronDown /> : <FiChevronUp />}
        Gespeicherte Kalkulationen
        {error ? (
          <span className="bg-red-600 px-2 py-0.5 rounded-full text-xs">Fehler</span>
        ) : loading ? (
          <span className="bg-gray-600 px-2 py-0.5 rounded-full text-xs">Laedt ...</span>
        ) : (
          <span className="bg-teal-600 px-2 py-0.5 rounded-full text-xs">{kalkulationen.length}</span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="bg-white border-t shadow-lg overflow-hidden"
          >
            <div className="max-h-48 overflow-y-auto p-4">
              {error && (
                <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700 flex items-center justify-between gap-3">
                  <span>{error}</span>
                  {onReload && (
                    <button onClick={onReload} className="px-3 py-1 rounded bg-white border border-red-300 text-xs hover:bg-red-100">
                      Erneut laden
                    </button>
                  )}
                </div>
              )}
              {loading && !error && (
                <p className="text-sm text-gray-500 mb-3">Kalkulationen werden geladen ...</p>
              )}
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="pb-2">Nr.</th>
                    <th className="pb-2">PLZ</th>
                    <th className="pb-2">Verbrauch</th>
                    <th className="pb-2">Datum</th>
                    <th className="pb-2">Erstellt von</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {kalkulationen.map(k => (
                    <tr key={k.id} className="border-b hover:bg-gray-50">
                      <td className="py-2 font-mono text-xs">{k.kalkulation_number}</td>
                      <td className="py-2">{k.plz}</td>
                      <td className="py-2">{k.verbrauch?.toLocaleString('de-DE')} kWh</td>
                      <td className="py-2">{k.created_at ? new Date(k.created_at).toLocaleDateString('de-DE') : '—'}</td>
                      <td className="py-2 text-gray-600">{k.created_by_name || '—'}</td>
                      <td className="py-2 text-right">
                        <button onClick={() => onLoad(k.id)} className="text-teal-600 hover:text-teal-800 mr-2 text-xs">Laden</button>
                        <button onClick={() => onDelete(k.id)} className="text-red-400 hover:text-red-600 text-xs">
                          <FiTrash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ============================================================
// Hauptkomponente
// ============================================================
const Kalkulator = () => {
  const { leadId } = useParams();
  const [searchParams] = useSearchParams();
  const { getLeadById } = useLeads();
  const {
    formData, step, provision, result, comparisonTable, filteredResults,
    selectedSuppliers, savedKalkulationen, kalkulationenLoading, kalkulationenError,
    isCalculating, calcError, isSaving, saveError, suppliersLoading,
    priceMode, futures,
    updateForm, setPriceMode, calculate, updateProvision, toggleSupplier,
    saveKalkulation, loadKalkulation, deleteKalkulation, reloadKalkulationen,
    reset, goToStep, setStep,
  } = useKalkulator();

  const [savedKalkulation, setSavedKalkulation] = useState(null);
  const [customerPrefill, setCustomerPrefill] = useState(null);

  // Lead-Daten vorausfuellen (Aufruf aus Lead-Detail)
  useEffect(() => {
    if (!leadId) return;
    const lead = getLeadById(leadId);
    if (!lead) return;
    const c = lead.company || {};
    const ct = lead.contact || {};
    if (c.plz) updateForm('plz', String(c.plz));
    if (c.annual_consumption_kwh) updateForm('verbrauch', String(c.annual_consumption_kwh));
    if (c.industry) updateForm('kurzbezeichnung', c.name || '');
    setCustomerPrefill({
      contact_name: [ct.first_name, ct.last_name].filter(Boolean).join(' '),
      company_name: c.name || '',
      contact_phone: ct.phone || '',
      contact_email: ct.email || '',
      // Anschrift: befuellt spaeter das Vollmacht-Formular des Kunden vor.
      // Die PLZ kommt bewusst mit: die PLZ der Kalkulation kann eine andere sein
      // (sie bestimmt nur das Netzgebiet und wird im Rechner frei geaendert).
      strasse: c.street || '',
      ort: c.city || '',
      kunde_plz: c.plz ? String(c.plz) : '',
      current_price_ct_kwh: c.current_price_ct_kwh || null,
      current_supplier: c.current_supplier || '',
      lead_id: lead.id,
    });
  }, [leadId, getLeadById, updateForm]);

  // Prefill aus GHL-Query-Parametern (Aufruf via Magic Link / Custom Tab).
  // Greift nur, wenn KEIN leadId vorhanden ist (sonst hat der Lead Vorrang).
  useEffect(() => {
    if (leadId) return;
    const get = (k) => {
      const v = searchParams.get(k);
      return v && v.trim() && !v.includes('{{') ? v.trim() : '';
    };
    const cid = get('cid');
    const company = get('company');
    const plz = get('plz');
    const verbrauch = get('verbrauch').replace(/[.\s]/g, '').replace(',', '.');
    const preis = get('preis').replace(',', '.');
    const supplier = get('supplier');
    const name = get('name');
    const email = get('email');
    const phone = get('phone');
    if (!cid && !company && !plz && !verbrauch) return; // keine GHL-Daten

    if (plz) updateForm('plz', plz);
    if (verbrauch && !isNaN(Number(verbrauch))) updateForm('verbrauch', String(parseInt(verbrauch, 10)));
    if (company) updateForm('kurzbezeichnung', company);
    setCustomerPrefill({
      contact_name: name,
      company_name: company,
      contact_phone: phone,
      contact_email: email,
      current_price_ct_kwh: preis && !isNaN(Number(preis)) ? Number(preis) : null,
      current_supplier: supplier,
      ghl_contact_id: cid || null,
      lead_id: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadId, searchParams, updateForm]);

  // Speichern laeuft jetzt serverseitig -> async
  const handleSave = async () => {
    const saved = await saveKalkulation({ leadId: customerPrefill?.lead_id || null });
    if (saved) setSavedKalkulation(saved);
  };

  const handleLoad = async (id) => {
    const ok = await loadKalkulation(id);
    if (ok) setSavedKalkulation(null);
  };

  const handleReset = () => {
    reset();
    setSavedKalkulation(null);
  };

  return (
    <div className="space-y-6 pb-16">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Strom-Kalkulator</h1>
          <p className="text-gray-500 mt-1">Vergleichsrechnung fuer Gewerbestrom</p>
        </div>
        {step > 0 && (
          <Button variant="secondary" onClick={handleReset}>
            <FiRefreshCw size={14} className="mr-1" /> Neue Kalkulation
          </Button>
        )}
      </div>

      {/* Stepper */}
      <Stepper currentStep={step} onStepClick={goToStep} />

      {/* Steps */}
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.2 }}
        >
          {step === 0 && (
            <EingabeStep
              formData={formData}
              updateForm={updateForm}
              onCalculate={calculate}
              isCalculating={isCalculating}
              priceMode={priceMode}
              setPriceMode={setPriceMode}
              futures={futures}
              calcError={calcError}
            />
          )}

          {step === 1 && (
            <AufschlagStep
              result={result}
              provision={provision}
              onProvisionChange={updateProvision}
              selectedSuppliers={selectedSuppliers}
              onToggleSupplier={toggleSupplier}
              onNext={() => setStep(2)}
              onBack={() => setStep(0)}
            />
          )}

          {step === 2 && (
            <ZusammenfassungStep
              result={result}
              provision={provision}
              filteredResults={filteredResults}
              onSave={handleSave}
              onBack={() => setStep(1)}
              savedKalkulation={savedKalkulation}
              customerPrefill={customerPrefill}
              isSaving={isSaving}
              saveError={saveError}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Gespeicherte Kalkulationen */}
      <SavedKalkulationenBar
        kalkulationen={savedKalkulationen}
        onLoad={handleLoad}
        onDelete={deleteKalkulation}
        loading={kalkulationenLoading}
        error={kalkulationenError}
        onReload={reloadKalkulationen}
      />
    </div>
  );
};

export default Kalkulator;
